import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, DatabaseCategory } from '../lib/supabase';

interface Category {
  id: string;
  name: string;
  nameEn: string;
  icon: string;
  count?: number;
}

interface CategoriesContextType {
  categories: Category[];
  loading: boolean;
  refreshCategories: () => Promise<void>;
  refreshCategoryCounts: () => Promise<void>;
  addCategory: (category: Omit<Category, 'id'>) => Promise<void>;
  updateCategory: (id: string, category: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

export function CategoriesProvider({ children }: { children: ReactNode }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Get property counts per category
      const categoriesWithCount = await Promise.all(
        (data || []).map(async (cat: DatabaseCategory) => {
          const { count } = await supabase
            .from('properties')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', cat.id);
          
          return {
            id: cat.id,
            name: cat.name,
            nameEn: cat.name_en || '',
            icon: cat.icon || 'Building2',
            count: count || 0,
          };
        })
      );
      
      setCategories(categoriesWithCount);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const addCategory = async (category: Omit<Category, 'id'>) => {
    const newCategoryData = {
      id: Date.now().toString(),
      name: category.name,
      name_en: category.nameEn,
      icon: category.icon,
    };
    
    const { data, error } = await supabase
      .from('categories')
      .insert(newCategoryData)
      .select()
      .single();
    
    if (error) throw error;
    
    setCategories(prev => [{
      id: data.id,
      name: data.name,
      nameEn: data.name_en,
      icon: data.icon,
      count: 0, // New categories start with 0 properties
    }, ...prev]);
  };

  const updateCategory = async (id: string, category: Partial<Category>) => {
    const { error } = await supabase
      .from('categories')
      .update({
        name: category.name,
        name_en: category.nameEn,
        icon: category.icon,
      })
      .eq('id', id);
    
    if (error) throw error;
    
    setCategories(prev => prev.map(c => 
      c.id === id ? { ...c, ...category } : c
    ));
  };

  const deleteCategory = async (id: string) => {
    const { error } = await supabase.from('categories').delete().eq('id', id);
    if (error) throw error;
    setCategories(prev => prev.filter(c => c.id !== id));
  };

  const refreshCategoryCounts = async () => {
    try {
      setLoading(true);
      
      // Get updated property counts for existing categories
      const categoriesWithUpdatedCount = await Promise.all(
        categories.map(async (cat) => {
          const { count } = await supabase
            .from('properties')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', cat.id);
          
          return {
            ...cat,
            count: count || 0,
          };
        })
      );
      
      setCategories(categoriesWithUpdatedCount);
    } catch (err) {
      console.error('Error refreshing category counts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <CategoriesContext.Provider value={{
      categories,
      loading,
      refreshCategories: fetchCategories,
      refreshCategoryCounts,
      addCategory,
      updateCategory,
      deleteCategory
    }}>
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategories() {
  const context = useContext(CategoriesContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
}
